/* eslint-disable sonarjs/cognitive-complexity */
import cn from 'classnames';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/common/Button';
import SidebarTooltip from 'src/components/common/SidebarTooltip';
import SkeletonButton from 'src/components/common/SkeletonButton';
import { icons } from 'src/configs/icons';
import { EnumRoutePaths } from 'src/configs/routes';
import { scrollToTop, setBodyOverflow, sliceIntoChunks } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useGameAction } from 'src/hooks/useGameAction';
import { MediaManager } from 'src/manager/MediaManager';
import { openMyAccountModal } from 'src/store/account-modal/actions';
import { resetSectionLoading, setGameBoxWidgetOpen, setSelectedCategory } from 'src/store/app/actions';
import { openAuthModal } from 'src/store/auth-modal/actions';
import { openDialog } from 'src/store/dialog/actions';
import { EnumDialogsKeys } from 'src/store/dialog/types';
import { setSelectedGame, setSelectedProvider, setSelectedTag } from 'src/store/games/actions';
import { NavigationItem } from 'src/types/res-dto/categories-res';
import { RootState } from 'src/types/store-types';
import { SvgIcon } from '../../common/SvgIcon';
import './styles.scss';

type Props = {
  settings: {
    view: { type: string; placement: string };
    priority: number;
    styles: { [key: string]: any };
    needLogin: false;
    splitCount: number;
    src: string;
    navigationCategories: [];
  };
};

const SideBarNavigation: FC<Props> = ({ settings }) => {
  const { navigationCategories, splitCount } = settings;

  const { t }: Translation = useTranslation();
  const navigate = useNavigate();

  const _setSelectedCategory = useConnectedAction(setSelectedCategory);
  const _selectedGame = useConnectedAction(setSelectedGame);
  const _openAuthModal = useConnectedAction(openAuthModal);
  const _setSelectedTag = useConnectedAction(setSelectedTag);
  const _openAccountModal = useConnectedAction(openMyAccountModal);
  const _setGameBoxWidgetOpen = useConnectedAction(setGameBoxWidgetOpen);
  const _setSelectedProvider = useConnectedAction(setSelectedProvider);
  const _resetSectionLoading = useConnectedAction(resetSectionLoading);
  const _openDialog = useConnectedAction(openDialog);

  const { sideBarMenuIsOpen, selectedCategory } = useSelector((state: RootState) => state.app);
  const { selectedTag } = useSelector((state: RootState) => state.games);
  const { categoriesData, navigation } = useSelector((state: RootState) => state.configs);
  const { user } = useSelector((state: RootState) => state.user);

  const [selectMenuItem, setSelectedMenuItem] = useState<string>('');
  const { shouldShowLimits, shouldShowKyc } = useGameAction();

  // Resolve the slug from the menu's own item list first (handles betting→sport), then redux navigation.
  const resolveCategorySlug = (categoryName: string): string =>
    (navigationCategories as unknown as NavigationItem[])?.find((c) => c?.name === categoryName)?.slug ||
    navigation?.find((n) => n.name === categoryName)?.slug ||
    categoryName;

  const selectTag = (tag: string): void => {
    const slug = resolveCategorySlug(selectMenuItem);
    navigate(`/category/${slug}`);
    _setSelectedProvider(['all_providers']);
    _setSelectedTag(tag);
    _setSelectedCategory(selectMenuItem);
  };

  const selectGame = (game: any): void => {
    const _game = navigation?.find((f) => f.name === game);
    _selectedGame({ id: _game?.gameId, gameWalletId: null });
  };

  const selectReferAFriend = (): void => {
    if (user) {
      _openAccountModal({ section: 'referAFriend', subSection: 'referAFriend', from: 'menu' });
    } else {
      setBodyOverflow('unset');
      _openAuthModal('login');
    }
  };

  const selectPromotion = (): void => {
    navigate(EnumRoutePaths.PROMOTIONS);
  };

  const selectCategory = (menuItem: string, type = 'category'): void => {
    _resetSectionLoading();
    // menuItem is the category name (data key); route is registered by slug
    const slug = resolveCategorySlug(menuItem);
    navigate(`/${type}/${slug}`);
    _setSelectedTag('');
    _setSelectedProvider(['all_providers']);
    _setSelectedCategory(menuItem);
  };

  const transformCategoryName = (menuItem: string): string => {
    let category;

    if (menuItem === 'betting') {
      category = 'sport';
    } else if (menuItem === 'tournament') {
      category = 'casinoTournament';
    } else {
      category = menuItem;
    }

    return category;
  };

  const checkSubType = useCallback(
    (_navItemData: NavigationItem, menuItem: string): void => {
      _setSelectedCategory(menuItem);
      selectCategory(menuItem);
    },
    [sideBarMenuIsOpen, categoriesData]
  );

  const actionByNavigationType = (type: string, menuItem: string, _navItemData: NavigationItem): void => {
    const category = transformCategoryName(menuItem);
    switch (type) {
      case 'category':
        if (
          (categoriesData && categoriesData[menuItem]?.tags?.length < 2) ||
          (categoriesData && categoriesData[menuItem]?.tags?.length > 2 && !hasChildren(menuItem))
        ) {
          selectCategory(menuItem);
        } else {
          checkSubType(_navItemData, menuItem);
        }

        setSelectedMenuItem(category === selectMenuItem ? '' : category);
        break;
      case 'sport':
      case 'liveSport':
      case 'tigerFortune':
        selectCategory(menuItem);
        break;
      case 'game':
        if (shouldShowKyc) {
          _openDialog({
            dialogType: EnumDialogsKeys.KYC_POPUP,
            dialogProps: { data: {} },
          });
        } else if (shouldShowLimits) {
          _openDialog({
            dialogType: EnumDialogsKeys.SET_LIMITS_AFTER_LOGIN_POPUP,
            dialogProps: { data: {} },
          });
        } else {
          selectGame(menuItem);
        }
        break;
      case 'content':
        selectCategory(menuItem, 'content');
        break;
      case 'race':
      case 'fsRace':
      case 'tombala':
      case 'cybersport':
      case 'safe-promo':
        selectCategory(menuItem);
        setSelectedMenuItem(category === selectMenuItem ? '' : category);
        break;
      case 'refer':
        selectReferAFriend();
        break;
      case 'promo':
        selectPromotion();
        setSelectedMenuItem(category === selectMenuItem ? '' : category);
        _setSelectedCategory(menuItem);
        break;
      case 'gamebox':
        _setGameBoxWidgetOpen();
        break;
      default:
        setSelectedMenuItem(category === selectMenuItem ? '' : category);
    }
  };

  const changeSelectedMenuItem = (menuItem: string, type = ''): void => {
    scrollToTop();
    if (!hasChildren(menuItem) && menuItem !== 'language') {
      const _navItemData = navigation.find((f) => f.name === menuItem) as NavigationItem;
      if (_navItemData?.needLogIn && !user) {
        setBodyOverflow('unset');
        _openAuthModal('login');
      } else {
        actionByNavigationType(type, menuItem, _navItemData);
      }
    } else {
      setSelectedMenuItem(menuItem === selectMenuItem ? '' : menuItem);
    }
  };

  const changeSelectedMenuIcon = (menuItem: string, type = ''): void => {
    scrollToTop();

    const _navItemData = navigation.find((f) => f.name === menuItem) as NavigationItem;

    if (_navItemData?.needLogIn && !user) {
      setBodyOverflow('unset');
      _openAuthModal('login');
    } else {
      actionByNavigationType(type, menuItem, _navItemData);
    }
  };

  const hasChildren = (category: string): boolean => {
    if (categoriesData) {
      return Boolean(
        categoriesData[category]?.tags?.length > 2 && navigation?.find((f) => f.name === category)?.hasHomeTag
      );
    }
    return false;
  };

  const menuIcon = useCallback((category: { imageSource: string; name: string }) => {
    if (category?.imageSource?.includes('.svg')) {
      return (
        <SvgIcon
          src={MediaManager.getSrcFromMediaAssets(category?.name + '.svg', '/categoryIcons/')}
          className={'category_icon'}
        />
      );
    }

    return (
      <img
        src={MediaManager.getSrcFromMediaAssets(category?.imageSource, '/categoryIcons/')}
        alt={category?.name}
        className={'img_icon'}
      />
    );
  }, []);

  const renderCategory = useCallback(
    (category: any) => {
      if (sideBarMenuIsOpen) {
        return (
          <React.Fragment key={category.name}>
            <Button
              className={cn('sidebar_section__item secondary-btn', {
                ['selected']: category.name === selectedCategory,
              })}
              onClick={() => changeSelectedMenuItem(category.name, category.type)}
            >
              <div className="sidebar_section__item__container">
                {menuIcon(category)}
                <span className="sidebar_section__item__text">
                  {t(`category_${category.name}${category.type === 'game' ? 'game' : ''}`)}
                </span>
              </div>
              {hasChildren(category.name) && (
                <SvgIcon
                  src={icons.arrowDown}
                  className={cn('arrow', { ['rotateArrow']: selectMenuItem === category.name })}
                />
              )}
              {category?.label && (
                <img
                  src={MediaManager.getSrcFromMediaAssets(category?.label, '/labels/')}
                  className="label-image"
                  alt="new-label"
                />
              )}
            </Button>
            {selectMenuItem === category.name && hasChildren(category.name) && (
              <ul className={'panel'}>
                {categoriesData &&
                  categoriesData[category.name]?.tags?.map((tag, j: number) => (
                    <li
                      key={j + tag.tagName}
                      className={selectedTag === tag.tagName ? 'selected' : ''}
                      onClick={() => selectTag(tag.tagName)}
                    >
                      {t(tag.tagName)}
                    </li>
                  ))}
              </ul>
            )}
          </React.Fragment>
        );
      } else {
        return (
          <SidebarTooltip title={t('category_' + category.name) || category.name} key={category.name}>
            <Button
              ariaLabelledby={t('category_' + category.name) || category.name}
              className={cn('secondary-btn', {
                ['selected']: category.name === selectedCategory,
              })}
              onClick={() => changeSelectedMenuIcon(category.name, category.type)}
            >
              <div className="sidebar_section__item__container">{menuIcon(category)}</div>
            </Button>
          </SidebarTooltip>
        );
      }
    },
    [selectMenuItem, categoriesData, selectedTag, sideBarMenuIsOpen, user]
  );

  const sideBarItemsArray = useMemo(() => {
    return sliceIntoChunks(navigationCategories, splitCount);
  }, [navigationCategories]);

  const renderItems = (navItems: any, index: number): JSX.Element => {
    return (
      <div className="sidebar-section" key={index}>
        {navItems
          .filter((f: { showInNavigation: boolean }) => f.showInNavigation)
          .map((navItem: NavigationItem) => renderCategory(navItem))}
      </div>
    );
  };

  useEffect(() => {
    setSelectedMenuItem(selectedCategory);
  }, [selectedCategory]);

  return (
    <>
      {sideBarItemsArray.length > 0 ? (
        <div className="sidebar__section">
          {sideBarItemsArray?.map((navItem: NavigationItem, index: number) => renderItems(navItem, index))}
        </div>
      ) : (
        <SkeletonButton className="navigation_wrapper__skeleton" />
      )}
    </>
  );
};
export default SideBarNavigation;
